import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { commitPendingMessage, setChannel, setBrowserUrl, setAppUrl, setDxaProps } from "../features/channelSlice";
import { detectUrlType, transformMagicUrlToDxa } from "../utils/urlDetector";
import { getNextAssignmentForCase } from "../utils/pegaApi";

export default function MessagingView() {
  const dispatch = useDispatch();
  const { 
    messages: reduxMessages, 
    smsJustReceived, 
    pendingMessage,
    channelConfig,
    currentChannel 
  } = useSelector((state) => state.channel);
  
  const brandConfig = useSelector((state) => state.channel.currentBrand);

  const meta = channelConfig[currentChannel] || {};

  // The local state now represents only the messages that are fully visible.
  const [displayedMessages, setDisplayedMessages] = useState(reduxMessages);
  const [isTyping, setIsTyping] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    // This effect now handles the entire animation sequence.
    if (smsJustReceived) {
      // 1. A new message is pending. Show the typing indicator.
      setIsTyping(true);
      
      const timer = setTimeout(() => {
        // 2. After 1.5s, the "typing" is done. Hide the indicator.
        setIsTyping(false);
        // 3. Dispatch an action to commit the pending message to the real list.
        dispatch(commitPendingMessage());
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [smsJustReceived, dispatch]);

  useEffect(() => {
    // This separate effect syncs the local display with the Redux state
    // whenever the canonical list changes. This happens AFTER the animation.
    setDisplayedMessages(reduxMessages);
  }, [reduxMessages]);


  // Effect to auto-scroll to the bottom instantly.
  useLayoutEffect(() => {
    const scrollContainer = scrollRef.current;
    if (scrollContainer) {
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    }
  }, [displayedMessages, isTyping]);


  // Drag-to-scroll logic (remains unchanged)
  const isDragging = useRef(false);
  const startY = useRef(0);
  const scrollTop = useRef(0);

  const handleMouseDown = (e) => {
    if (scrollRef.current && scrollRef.current.scrollTop > 0) {
      e.preventDefault();
    }
    isDragging.current = true;
    const y = e.touches ? e.touches[0].clientY : e.clientY;
    startY.current = y;
    scrollTop.current = scrollRef.current.scrollTop;
    if (scrollRef.current) {
      scrollRef.current.style.cursor = 'grabbing';
      scrollRef.current.style.userSelect = 'none';
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current || !scrollRef.current) return;
    e.preventDefault();
    const y = e.touches ? e.touches[0].clientY : e.clientY;
    const walk = y - startY.current;
    scrollRef.current.scrollTop = scrollTop.current - walk;
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    if (scrollRef.current) {
      scrollRef.current.style.cursor = 'grab';
      scrollRef.current.style.userSelect = 'auto';
    }
  };

  useEffect(() => {
    const scrollable = scrollRef.current;
    if (scrollable) {
      scrollable.addEventListener('mousedown', handleMouseDown);
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      scrollable.addEventListener('touchstart', handleMouseDown, { passive: true });
      window.addEventListener('touchmove', handleMouseMove);
      window.addEventListener('touchend', handleMouseUp);
    }

    return () => {
      if (scrollable) {
        scrollable.removeEventListener('mousedown', handleMouseDown);
      }
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleMouseMove);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, []);

  // Helper: detect URLs in text and return an array of {text, url} or plain text
  // Updated regex to properly capture query strings and all URL components
  const urlRegex = /\b((https?:\/\/)[^\s<>'"]+)/gi;
  
  const handleUrlClick = async (url) => {
    console.log('📎 URL clicked in SMS (raw):', url);
    
    const fullUrl = url.startsWith('http') ? url : `https://${url}`;
    console.log('📎 URL clicked in SMS (full):', fullUrl);
    
    const urlType = detectUrlType(fullUrl);
    console.log('📎 URL type detected:', urlType);
    
    if (urlType === 'dxa-proxy') {
      // Magic URL detected - transform to DXA and open in AppView
      console.log('🔮 Magic DXA URL detected, transforming...');
      console.log('🔮 Brand config available:', !!brandConfig, 'DXA config:', brandConfig?.dxa);
      
      // Check if brand config is available
      if (!brandConfig) {
        console.error('❌ Brand config not loaded yet');
        console.warn('💡 User needs to configure the app via Settings modal');
        console.warn('💡 Falling back to opening URL in browser');
        dispatch(setBrowserUrl(fullUrl));
        dispatch(setChannel('browser'));
        return;
      }
      
      let dxaConfig = transformMagicUrlToDxa(fullUrl, brandConfig);

      if (dxaConfig) {
        // If openAssignment was requested but no assignmentId provided, resolve it from the case
        if (dxaConfig.action === 'openAssignment' && !dxaConfig.assignmentId && dxaConfig.caseId) {
          console.log('🔍 openAssignment without assignmentId — resolving from case:', dxaConfig.caseId);
          const configId = parseInt(localStorage.getItem('selectedConfigId') ?? '-1');
          const assignmentId = await getNextAssignmentForCase(dxaConfig.caseId, configId);
          if (assignmentId) {
            console.log('✅ Resolved assignmentId:', assignmentId);
            dxaConfig = { ...dxaConfig, assignmentId, caseId: undefined };
          } else {
            console.warn('⚠️ Could not resolve assignmentId — proceeding with caseId only');
          }
        }
        console.log('✅ Transformed to DXA config:', dxaConfig);
        dispatch(setDxaProps(dxaConfig));
        dispatch(setChannel('app'));
      } else {
        console.error('❌ Failed to transform magic URL');
        console.warn('💡 This may be due to missing or invalid DXA config in brand');
        // Fallback to browser
        dispatch(setBrowserUrl(fullUrl));
        dispatch(setChannel('browser'));
      }
    } else if (urlType === 'dxa') {
      // Full DXA URL - open in AppView with URL as-is (preserving all params)
      console.log('🎯 Full DXA URL detected, opening in AppView with explicit URL');
      // Use setAppUrl to preserve ALL URL parameters including credentials
      dispatch(setAppUrl(fullUrl));
      dispatch(setChannel('app'));
    } else {
      // Regular URL - open in browser
      console.log('🌐 Regular URL, opening in browser');
      dispatch(setBrowserUrl(fullUrl));
      dispatch(setChannel('browser'));
    }
  };
  
  function parseTextWithLinks(text) {
    const elements = [];
    let lastIndex = 0;
    let match;
    
    // Reset regex lastIndex to avoid issues with global flag
    urlRegex.lastIndex = 0;
    
    while ((match = urlRegex.exec(text)) !== null) {
      const url = match[0];
      const start = match.index;
      
      console.log('🔗 URL detected in SMS:', url);
      
      if (start > lastIndex) {
        elements.push(text.slice(lastIndex, start));
      }
      // URL should already have http/https prefix from regex
      const fullUrl = url;
      
      console.log('🔗 Full URL for detection:', fullUrl);
      
      elements.push(
        <a
          key={start}
          href={fullUrl}
          style={{ color: '#2563eb', textDecoration: 'underline', cursor: 'pointer' }}
          onClick={e => {
            e.preventDefault();
            handleUrlClick(fullUrl);
          }}
        >
          {url}
        </a>
      );
      lastIndex = start + url.length;
    }
    if (lastIndex < text.length) {
      elements.push(text.slice(lastIndex));
    }
    return elements;
  }

  // Determine the alignment and color of the typing indicator
  const typingIndicatorAlign = pendingMessage?.sender === 'me' ? 'self-end' : 'self-start';
  const typingIndicatorBg = pendingMessage?.sender === 'me' ? '#007aff' : '#e5e5ea';
  const typingDotColor = pendingMessage?.sender === 'me' ? 'white' : 'grey';

  return (
    <div className="bg-white h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-center p-4 border-b relative">
          <div className="text-center">
              <h1 className="text-lg font-bold text-gray-800">{meta.title || 'Messages'}</h1>
              {meta.subtitle && <p className="text-sm text-gray-500">{meta.subtitle}</p>}
          </div>
      </div>

      {/* Messages container */}
      <div
        ref={scrollRef}
        className={`flex-1 overflow-y-auto no-scrollbar p-4 flex flex-col space-y-3`}
        style={{
          WebkitOverflowScrolling: 'touch',
          cursor: 'grab',
          userSelect: 'auto',
          scrollbarWidth: 'none', /* For Firefox */
        }}
      >
        {/* Render the local `displayedMessages` state */}
        {displayedMessages.map((msg, index) => (
          <div
            key={index}
            className={`max-w-[80%] p-3 rounded-2xl shadow-sm break-words ${
              msg.sender === "me"
                ? "self-end text-white"
                : "self-start text-black"
            }`}
            style={{
              backgroundColor: msg.sender === 'me' ? '#007aff' : '#e5e5ea',
              fontSize: '1rem',
            }}
          >
            {parseTextWithLinks(msg.text)}
          </div>
        ))}
        {isTyping && (
          <div className={typingIndicatorAlign}>
            <div
              className="p-3 rounded-2xl shadow-sm flex items-center justify-center"
              style={{ backgroundColor: typingIndicatorBg, minHeight: '44px' }} // Ensure consistent height
            >
              <div className="flex space-x-1">
                <span
                  className="h-2 w-2 rounded-full animate-bounce-1"
                  style={{ backgroundColor: typingDotColor }}
                ></span>
                <span
                  className="h-2 w-2 rounded-full animate-bounce-2"
                  style={{ backgroundColor: typingDotColor }}
                ></span>
                <span
                  className="h-2 w-2 rounded-full animate-bounce-3"
                  style={{ backgroundColor: typingDotColor }}
                ></span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
